import { Suspense } from 'react'
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarRail,
  Button,
  Spinner,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@anytimefitness/pb-react-ui'
import {
  ChartColumn,
  ChevronDownIcon,
  IdCard,
  SquareUser,
  User,
  Users,
  type LucideIcon,
} from 'lucide-react'
import Image from 'next/image'
import OTFLogo from '@/assets/images/OTF_Secondary_Logo_Bone_RGB.png'
import { getStudios } from '@/server/actions/studios'
import SidebarLink from './sidebar-link'
import StudioSelectCombobox from './studio-select-combobox'

export interface SidebarItem {
  title: string
  url: string
}

interface SidebarNavGroup {
  title: string
  Icon: LucideIcon
  items: SidebarItem[]
}

export const sidebarContent: SidebarNavGroup[] = [
  {
    title: 'Tasks',
    Icon: SquareUser,
    items: [
      {
        title: 'Lead Follow Up',
        url: '/lead-management',
      },
      {
        title: 'Member Follow Up',
        url: '/member-management',
      },
      {
        title: 'Revenue Follow Up',
        url: '/revenue-management',
      },
    ],
  },
  {
    title: 'Classes',
    Icon: Users,
    items: [
      {
        title: 'Class Roster',
        url: '/roster',
      },
    ],
  },
  {
    title: 'Customers',
    Icon: User,
    items: [
      {
        title: 'User Profiles',
        url: '/user-profiles',
      },
    ],
  },
  {
    title: 'Reporting',
    Icon: ChartColumn,
    items: [
      {
        title: 'Studio',
        url: '/studio-performance',
      },
      {
        title: 'Portfolio',
        url: '/portfolio-performance',
      },
      {
        title: 'Inter-Studio',
        url: '/inter-studio',
      },
    ],
  },
  {
    title: 'Staff',
    Icon: IdCard,
    items: [
      {
        title: 'Identity Management',
        url: '/identity-management',
      },
    ],
  },
]

export default function AppSidebar() {
  const studiosPromise = getStudios()

  return (
    <Sidebar variant="sidebar">
      <SidebarHeader className="px-8 py-2">
        <Image src={OTFLogo} alt="OTF Logo" className="p-6" />
        <div className="mt-3 flex justify-center">
          <Suspense
            fallback={
              <Button
                variant="outline"
                disabled
                aria-disabled
                className="w-3xs border-primary bg-primary text-sidebar-accent-foreground"
              >
                <Spinner className="stroke-sidebar-accent-foreground" />
              </Button>
            }
          >
            <StudioSelectCombobox studiosPromise={studiosPromise} />
          </Suspense>
        </div>
      </SidebarHeader>
      <SidebarContent className="px-2">
        {sidebarContent.map(({ title, items, Icon }) => (
          <Collapsible key={title} className="group/collapsible" defaultOpen>
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton className="my-1">
                  <div className="mr-1">
                    <ChevronDownIcon
                      size={20}
                      className="opacity-75 transition-transform group-data-[state=closed]/collapsible:-rotate-90"
                    />
                  </div>
                  <div className="mr-1">
                    <Icon size={24} />
                  </div>
                  {title}
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub className="border-none">
                  {items.map(({ title, url }) => (
                    <SidebarLink key={title} title={title} url={url} />
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  )
}
